import { useState } from "react";
import { CompleteCliffNote } from "@/features/trending/types/CliffNote";
import { Article } from "@/features/search/types/search";
import {
  getEmbedding,
  fetchArticlesByEmbedding,
  fetchArticlesByTag,
} from "@/api/utils";
import { generateCliffNote } from "@/api/utils";

export const useCliffNote = () => {
  const [cliffNote, setCliffNote] = useState<CompleteCliffNote>(() => {
    const saved = localStorage.getItem("cliffNote");
    return saved ? JSON.parse(saved) : null;
  });
  const [cliffNoteArticles, setCliffNoteArticles] = useState<Article[]>(() => {
    const saved = localStorage.getItem("cliffNoteArticles");
    return saved ? JSON.parse(saved) : [];
  });
  const [isFetchingArticles, setIsFetchingArticles] = useState(false);
  const [isGeneratingCliffNote, setIsGeneratingCliffNote] = useState(false);
  const MAX_ARTICLES = 5;

  const handleCliffNoteSearch = async (query: string, timeRange: number) => {
    try {
      const embedding = await getEmbedding(query);
      if (!embedding) {
        throw new Error("Failed to generate embedding");
      }
      setIsFetchingArticles(true);

      const searchResults = await fetchArticlesByEmbedding({
        embedding,
        daysAgo: timeRange,
        searchMode: "summary",
        matchCount: MAX_ARTICLES,
      });

      setCliffNoteArticles(searchResults);
      localStorage.setItem("cliffNoteArticles", JSON.stringify(searchResults));
      setIsGeneratingCliffNote(true);
      const note = await createCliffNote(query, searchResults);

      if (note) {
        const fullNote = {
          ...note,
          searchQuery: query,
        };
        setCliffNote(fullNote);
        localStorage.setItem("cliffNote", JSON.stringify(fullNote));
      }
    } catch (error) {
      console.error("Error generating cliff note:", error);
      setCliffNoteArticles([]);
    } finally {
      setIsFetchingArticles(false);
      setIsGeneratingCliffNote(false);
    }
  };

  const handleTagSelect = async (tag: string, timeRange: number) => {
    try {
      setIsFetchingArticles(true);
      const articles = await fetchArticlesByTag(tag, timeRange);
      setCliffNoteArticles(articles);
      localStorage.setItem("cliffNoteArticles", JSON.stringify(articles));
      setIsGeneratingCliffNote(true);
      const note = await generateCliffNote(tag, articles);
      setCliffNote(note);
      localStorage.setItem("cliffNote", JSON.stringify(note));
    } catch (error) {
      console.error("Error in handleTagSelect:", error);
      setCliffNoteArticles([]);
    } finally {
      setIsFetchingArticles(false);
      setIsGeneratingCliffNote(false);
    }
  };

  const createCliffNote = async (
    searchQuery: string,
    searchResults?: Article[]
  ) => {
    try {
      setIsGeneratingCliffNote(true);

      //Step 1: Check if there are any articles to feed to the AI
      if (!searchResults || searchResults.length === 0) {
        console.log(
          "No articles found for search query at createCliffNote function:",
          searchQuery
        );
        setIsGeneratingCliffNote(false);
        return null;
      }
      //Step 2: Contextualize the articles

      const articlesContext = searchResults
        .map(
          (article: Article) =>
            `Title: ${article.title},
       Summary: ${article.summary},
       Publication: ${article.author},
       Publish Date: ${article.publish_date},
       URL: ${article.link}`
        )
        .join("\n");

      //Step 3: Feed the articles to the AI to generate a cliff note
      const response = await fetch(
        "https://openai-worker.marsescobin.workers.dev/api/cliff-note",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            articlesContext,
            searchQuery,
          }),
        }
      );
      if (!response.ok) {
        setIsGeneratingCliffNote(false);
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return {
        searchQuery,
        openAICliffNote: data.openAICliffNote,
      };
    } catch (error) {
      console.error("Error generating cliff note:", error);
      setIsGeneratingCliffNote(false);
      return null;
    } finally {
      setIsGeneratingCliffNote(false);
      setIsFetchingArticles(false);
    }
  };

  return {
    cliffNote,
    cliffNoteArticles,
    handleCliffNoteSearch,
    createCliffNote,
    isFetchingArticles,
    isGeneratingCliffNote,
    handleTagSelect,
    MAX_ARTICLES,
  };
};
